// Set Item System - equipment set bonuses
//
// Defines item sets where equipping multiple pieces from the same set
// grants escalating bonuses. Integrates with the StatCalculator via
// getSetBonusModifiers() which returns Modifier-compatible objects.

import { Modifier, StatType, ModifierType } from '@shared/modifiers/ModifierSystem';

// ========================
// Interfaces
// ========================

export interface SetModifier {
  stat: string;     // e.g. 'damage', 'maxHp', 'movementSpeed', etc.
  value: number;
  isPercent: boolean;
}

export interface SetBonus {
  piecesRequired: number;
  modifiers: SetModifier[];
  specialEffect?: string; // Description of special effect
}

export interface ItemSet {
  id: string;
  name: string;
  description: string;
  pieces: string[]; // Item base IDs that belong to this set
  bonuses: SetBonus[];
}

// ========================
// Set Definitions
// ========================

/** 1. Alp Er Tunga Seti (Warrior - 4 pieces) */
const ALP_ER_TUNGA_SET: ItemSet = {
  id: 'alp_er_tunga',
  name: 'Alp Er Tunga Seti',
  description: 'Efsanevi savaşçı Alp Er Tunga\'nın zırh takımı. Fiziksel güce ve dayanıklılığa odaklanır.',
  pieces: [
    'set_alp_ertunga_kilic',
    'set_alp_ertunga_demir_zirh',
    'set_alp_ertunga_demir_tolgasi',
    'set_alp_ertunga_demir_eldiveni',
  ],
  bonuses: [
    {
      piecesRequired: 2,
      modifiers: [{ stat: 'damage', value: 15, isPercent: true }],
    },
    {
      piecesRequired: 3,
      modifiers: [
        { stat: 'maxHp', value: 500, isPercent: false },
        { stat: 'armor', value: 20, isPercent: false },
      ],
    },
    {
      piecesRequired: 4,
      modifiers: [
        { stat: 'lifeLeech', value: 5, isPercent: false },
        { stat: 'hpRegen', value: 30, isPercent: true },
      ],
      specialEffect: 'Her öldürmede %5 HP iyileşme',
    },
  ],
};

/** 2. Asena Seti (Börü - 4 pieces) */
const ASENA_SET: ItemSet = {
  id: 'asena',
  name: 'Asena Seti',
  description: 'Dişi kurdun ruhuyla kutsanmış ekipman. Hız ve kritik vuruşa odaklanır.',
  pieces: [
    'set_asena_silah',
    'set_asena_cizme',
    'set_asena_eldiven',
    'set_asena_muska',
  ],
  bonuses: [
    {
      piecesRequired: 2,
      modifiers: [{ stat: 'moveSpeed', value: 20, isPercent: true }],
    },
    {
      piecesRequired: 3,
      modifiers: [{ stat: 'critChance', value: 25, isPercent: true }],
    },
    {
      piecesRequired: 4,
      modifiers: [
        { stat: 'moveSpeed', value: 30, isPercent: true },
        { stat: 'attackSpeed', value: 15, isPercent: true },
      ],
      specialEffect: 'Öldürünce 3sn kurt hızı (%50 speed)',
    },
  ],
};

/** 3. Kam Bilgeliği Seti (Shaman - 4 pieces) */
const KAM_BILGELIGI_SET: ItemSet = {
  id: 'kam_bilgeligi',
  name: 'Kam Bilgeliği Seti',
  description: 'Kadim şamanların bilgeliğini taşıyan kutsal ekipman. Büyü gücüne odaklanır.',
  pieces: [
    'set_kam_bilgeligi_asa',
    'set_kam_bilgeligi_cubbe',
    'set_kam_bilgeligi_tolga',
    'set_kam_bilgeligi_yuzuk',
  ],
  bonuses: [
    {
      piecesRequired: 2,
      modifiers: [{ stat: 'manaRegen', value: 30, isPercent: true }],
    },
    {
      piecesRequired: 3,
      modifiers: [{ stat: 'elementalDamage', value: 20, isPercent: true }],
    },
    {
      piecesRequired: 4,
      modifiers: [
        { stat: 'spellDamage', value: 30, isPercent: true },
        { stat: 'castSpeed', value: 15, isPercent: true },
      ],
      specialEffect: 'Büyüler %15 şansla 2x cast',
    },
  ],
};

/** 4. Göktürk Savaşçısı Seti (General - 4 pieces) */
const GOKTURK_SET: ItemSet = {
  id: 'gokturk',
  name: 'Göktürk Savaşçısı Seti',
  description: 'Göktürk ordusunun seçkin savaşçılarına verilen ekipman. Genel güce odaklanır.',
  pieces: [
    'set_gokturk_silah',
    'set_gokturk_zirh',
    'set_gokturk_cizme',
    'set_gokturk_kemer',
  ],
  bonuses: [
    {
      piecesRequired: 2,
      modifiers: [{ stat: 'allResist', value: 10, isPercent: true }],
    },
    {
      piecesRequired: 3,
      modifiers: [{ stat: 'expGain', value: 20, isPercent: true }],
    },
    {
      piecesRequired: 4,
      modifiers: [
        { stat: 'expGain', value: 25, isPercent: true },
        { stat: 'itemRarity', value: 20, isPercent: true },
        { stat: 'itemQuantity', value: 15, isPercent: true },
      ],
      specialEffect: 'Her 5 öldürmede mini-shrine',
    },
  ],
};

/** 5. Bozkır Okçusu Seti (Archer - 4 pieces) */
const BOZKIR_SET: ItemSet = {
  id: 'bozkir',
  name: 'Bozkır Okçusu Seti',
  description: 'Bozkırın en iyi okçularının kullandığı ekipman. Menzilli saldırıya odaklanır.',
  pieces: [
    'set_bozkir_yay',
    'set_bozkir_zirh',
    'set_bozkir_eldiven',
    'set_bozkir_cizme',
  ],
  bonuses: [
    {
      piecesRequired: 2,
      modifiers: [{ stat: 'projectileCount', value: 2, isPercent: false }],
    },
    {
      piecesRequired: 3,
      modifiers: [{ stat: 'attackSpeed', value: 30, isPercent: true }],
    },
    {
      piecesRequired: 4,
      modifiers: [
        { stat: 'areaOfEffect', value: 30, isPercent: true },
        { stat: 'damage', value: 20, isPercent: true },
      ],
      specialEffect: 'Oklar %20 şansla patlayıcı AOE',
    },
  ],
};

/** 6. Ruh Yürüyücü Seti (Endgame - 3 pieces) */
const RUH_YURUYUCU_SET: ItemSet = {
  id: 'ruh_yuruyucu',
  name: 'Ruh Yürüyücü Seti',
  description: 'Ruh aleminin kapılarını aralayan esrarengiz eşyalar. Ölümü alt edecek güce sahip.',
  pieces: [
    'set_ruh_yuruyucu_muska',
    'set_ruh_yuruyucu_yuzuk',
    'set_ruh_yuruyucu_pelerin',
  ],
  bonuses: [
    {
      piecesRequired: 2,
      modifiers: [{ stat: 'damage', value: 15, isPercent: true }],
    },
    {
      piecesRequired: 3,
      modifiers: [
        { stat: 'maxHp', value: 30, isPercent: true },
        { stat: 'hpRegen', value: 50, isPercent: true },
        { stat: 'energyShield', value: 200, isPercent: false },
      ],
      specialEffect: 'Ölümde 1 kez canlan %30 HP, 5dk CD',
    },
  ],
};

// ========================
// Registry
// ========================

const ALL_SETS: ItemSet[] = [
  ALP_ER_TUNGA_SET,
  ASENA_SET,
  KAM_BILGELIGI_SET,
  GOKTURK_SET,
  BOZKIR_SET,
  RUH_YURUYUCU_SET,
];

/** Lookup map: item base ID -> ItemSet */
const ITEM_TO_SET_MAP: Map<string, ItemSet> = new Map();

// Build the lookup map
for (const set of ALL_SETS) {
  for (const pieceId of set.pieces) {
    ITEM_TO_SET_MAP.set(pieceId, set);
  }
}

// ========================
// Public API
// ========================

/**
 * Get all defined item sets.
 */
export function getAllSets(): ItemSet[] {
  return [...ALL_SETS];
}

/**
 * Check if an item belongs to a set, return set info.
 * @param itemBaseId The base ID of the item (EQUIPMENT_BASES key or setId-derived)
 */
export function getItemSet(itemBaseId: string): ItemSet | null {
  return ITEM_TO_SET_MAP.get(itemBaseId) || null;
}

/**
 * Get a set by its set ID.
 */
export function getSetById(setId: string): ItemSet | null {
  return ALL_SETS.find(s => s.id === setId) || null;
}

/**
 * Calculate active set bonuses for a collection of equipped items.
 * @param equippedItemBaseIds Array of base IDs for all equipped items
 * @returns Active set bonuses grouped by set
 */
export function calculateSetBonuses(equippedItemBaseIds: string[]): {
  activeSetBonuses: Array<{ set: ItemSet; activePieces: number; bonuses: SetBonus[] }>;
} {
  // Count pieces per set
  const setPieceCounts: Map<string, number> = new Map();
  const setMap: Map<string, ItemSet> = new Map();

  for (const baseId of equippedItemBaseIds) {
    const set = ITEM_TO_SET_MAP.get(baseId);
    if (set) {
      const current = setPieceCounts.get(set.id) || 0;
      setPieceCounts.set(set.id, current + 1);
      setMap.set(set.id, set);
    }
  }

  const activeSetBonuses: Array<{ set: ItemSet; activePieces: number; bonuses: SetBonus[] }> = [];

  for (const [setId, pieceCount] of setPieceCounts.entries()) {
    const set = setMap.get(setId)!;
    const activeBonuses = set.bonuses.filter(b => b.piecesRequired <= pieceCount);

    if (activeBonuses.length > 0) {
      activeSetBonuses.push({
        set,
        activePieces: pieceCount,
        bonuses: activeBonuses,
      });
    }
  }

  return { activeSetBonuses };
}

/**
 * Get set bonus modifiers as a flat Modifier list for StatCalculator integration.
 * Converts SetModifier entries into the Modifier format used by ModifierSystem.
 *
 * @param equippedItemBaseIds Array of base IDs for all equipped items
 * @returns Array of Modifier objects ready to be added to StatCalculator
 */
export function getSetBonusModifiers(equippedItemBaseIds: string[]): Modifier[] {
  const { activeSetBonuses } = calculateSetBonuses(equippedItemBaseIds);
  return convertActiveSetBonusesToModifiers(activeSetBonuses);
}

/**
 * Calculate active set bonuses using setId values from equipped items.
 * This is the preferred method when working with equipped EquipmentItem objects,
 * since items store their setId directly.
 *
 * @param equippedSetIds Array of setId values from equipped items (may contain duplicates and undefined)
 * @returns Active set bonuses grouped by set
 */
export function calculateSetBonusesBySetIds(equippedSetIds: (string | undefined)[]): {
  activeSetBonuses: Array<{ set: ItemSet; activePieces: number; bonuses: SetBonus[] }>;
} {
  const setPieceCounts: Map<string, number> = new Map();

  for (const setId of equippedSetIds) {
    if (!setId) continue;
    const current = setPieceCounts.get(setId) || 0;
    setPieceCounts.set(setId, current + 1);
  }

  const activeSetBonuses: Array<{ set: ItemSet; activePieces: number; bonuses: SetBonus[] }> = [];

  for (const [setId, pieceCount] of setPieceCounts.entries()) {
    const set = ALL_SETS.find(s => s.id === setId);
    if (!set) continue;

    const activeBonuses = set.bonuses.filter(b => b.piecesRequired <= pieceCount);
    if (activeBonuses.length > 0) {
      activeSetBonuses.push({
        set,
        activePieces: pieceCount,
        bonuses: activeBonuses,
      });
    }
  }

  return { activeSetBonuses };
}

/**
 * Get set bonus modifiers using setId values from equipped items.
 * This is the preferred integration point for Player.rebuildStatCalculator().
 *
 * @param equippedSetIds Array of setId values from equipped items
 * @returns Array of Modifier objects ready to be added to StatCalculator
 */
export function getSetBonusModifiersBySetIds(equippedSetIds: (string | undefined)[]): Modifier[] {
  const { activeSetBonuses } = calculateSetBonusesBySetIds(equippedSetIds);
  return convertActiveSetBonusesToModifiers(activeSetBonuses);
}

/**
 * Convert active set bonus entries to Modifier array.
 */
function convertActiveSetBonusesToModifiers(
  activeSetBonuses: Array<{ set: ItemSet; activePieces: number; bonuses: SetBonus[] }>
): Modifier[] {
  const modifiers: Modifier[] = [];

  for (const entry of activeSetBonuses) {
    for (const bonus of entry.bonuses) {
      for (const setMod of bonus.modifiers) {
        const statType = setMod.stat as StatType;
        const modType: ModifierType = setMod.isPercent ? 'increased' : 'flat';

        modifiers.push({
          stat: statType,
          type: modType,
          value: setMod.value,
          source: `set_${entry.set.id}`,
        });
      }
    }
  }

  return modifiers;
}
